import Sentry from "@similarweb/sentry-logger";

import { useApiQuery } from "app/modules/api-client";
import type { Args } from "app/modules/api-client/queryTypes";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { getAiInsightsAccess } from "./getAiInsightsAccess";
import { isAiInsightsUnsupportedQueryError } from "./getAiInsightsDisplay";
import { getAiInsightsViewModel } from "./getAiInsightsViewModel";

const GALLERY_PAGE_SIZE = 100;
const GALLERY_SORT = "Recent";

type TrafficParams = Args<"AiRefferrals", "aiResearchAiTrafficOverviewList">[0];
type AdsParams = Args<"AdIntAiAds", "adintelligenceAiadsGalleryList">[0];

type UseAiInsightsParams = {
    domain: string;
    country: number;
    duration: string;
    webSource: string;
    from?: string;
    to?: string;
    isWindow?: boolean;
};

const trackQueryError = (error: unknown, message: string, extra: Record<string, unknown>): false => {
    if (isAiInsightsUnsupportedQueryError(error)) {
        return false;
    }

    Sentry.errorTracker({
        e: error instanceof Error ? error : new Error(JSON.stringify(error)),
        message,
        level: "error",
        params: { extra },
    });

    return false;
};

const isQueryError = (error: unknown): boolean => Boolean(error) && !isAiInsightsUnsupportedQueryError(error);

export const useAiInsights = ({
    domain,
    country,
    duration,
    webSource,
    from,
    to,
    isWindow: isWindowParam,
}: UseAiInsightsParams) => {
    const { canFetchTraffic, canFetchAds, isTrafficLocked, isAdsLocked, canShowCard, isUpgrade } = getAiInsightsAccess({
        domain,
        isUSState: CountryService.isUSState(country),
    });
    const durationOrDefault = duration || BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const { iso, isWindow: isWindowFromDuration } = DurationService.getDurationData(durationOrDefault).forAPI;
    const resolvedFrom = from ?? iso.from;
    const resolvedTo = to ?? iso.to;
    const resolvedIsWindow = isWindowParam ?? isWindowFromDuration;

    const trafficParams = {
        keys: [domain],
        from: resolvedFrom,
        to: resolvedTo,
        webSource: webSource as TrafficParams["webSource"],
        country,
    };

    const adsParams = {
        domain,
        country,
        from: resolvedFrom,
        to: resolvedTo,
        isWindow: resolvedIsWindow,
        ...(resolvedIsWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        page: 1,
        pageSize: GALLERY_PAGE_SIZE,
        sort: GALLERY_SORT,
    } satisfies AdsParams;

    const trafficQuery = useApiQuery(
        {
            tag: "AiRefferrals",
            methodName: "aiResearchAiTrafficOverviewList",
            options: {
                enabled: canFetchTraffic,
                throwOnError: (error: unknown) =>
                    trackQueryError(error, "Error fetching AI insights traffic", {
                        keys: trafficParams.keys,
                        from: trafficParams.from,
                        to: trafficParams.to,
                        country: trafficParams.country,
                        webSource: trafficParams.webSource,
                    }),
            },
        },
        trafficParams,
    );

    const adsQuery = useApiQuery(
        {
            tag: "AdIntAiAds",
            methodName: "adintelligenceAiadsGalleryList",
            options: {
                enabled: canFetchAds,
                throwOnError: (error: unknown) =>
                    trackQueryError(error, "Error fetching AI insights ads", {
                        domain,
                        country,
                        from: adsParams.from,
                        to: adsParams.to,
                    }),
            },
        },
        adsParams,
    );

    // `enabled` stops new fetches; these guards omit stale cached data/errors when access context changes (e.g. US state).
    const ads = canFetchAds ? (adsQuery.data?.data ?? []) : [];
    const observationCount = adsQuery.data?.totalCount ?? ads.length;
    const trafficRecords = canFetchTraffic ? trafficQuery.data?.records : undefined;
    const isAdsGalleryTruncated = canFetchAds && ads.length < observationCount;
    const isTrafficError = canFetchTraffic && trafficQuery.isError && isQueryError(trafficQuery.error);
    const isAdsError = canFetchAds && adsQuery.isError && isQueryError(adsQuery.error);

    const viewModel = getAiInsightsViewModel({
        trafficRecords,
        ads,
        isAdsGalleryTruncated,
        isTrafficError,
        isAdsError,
        isTrafficLocked,
        isAdsLocked,
        isUpgrade,
    });

    return {
        canShowCard,
        isLoading: trafficQuery.isLoading || adsQuery.isLoading,
        status: viewModel.status,
        content: viewModel.content,
    };
};
